/**
 * Potjes that were started on another phone.
 *
 * Only the ones this device does not already have: a potje you can see under
 * "Verder spelen" needs no taking over, and showing it twice would make you
 * wonder which of the two is the real one.
 *
 * Silent when there is nothing to offer, and silent when there is no network —
 * an empty panel with an error in it on the home screen is worse than no panel,
 * because the home screen is also what you look at on a campsite with no signal.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Panel } from '@/components/ui';
import DeleteButton from '@/components/DeleteButton';
import { claimGame, fetchResumable, type ResumableGame } from '@/lib/resume';
import { removeGame, type ScoreKind } from '@/lib/removeGame';
import { formatDateTime } from '@/lib/labels';

const GAME_LABEL: Record<string, string> = {
  boerenbridge: 'Boerenbridge',
  klaverjas: 'Klaverjassen',
};

export default function ResumeFromServer({ known }: { known: string[] }) {
  const navigate = useNavigate();
  const [games, setGames] = useState<ResumableGame[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void fetchResumable()
      .then((all) => {
        if (live) setGames(all);
      })
      .catch(() => {
        // No signal, or not signed in yet. Nothing to say about it here.
      });
    return () => {
      live = false;
    };
  }, []);

  const elsewhere = games.filter((game) => !known.includes(game.id));
  if (elsewhere.length === 0) return null;

  async function take(game: ResumableGame) {
    setBusy(game.id);
    setError(null);
    try {
      navigate(await claimGame(game.id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Overnemen mislukt.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <Panel title="Op een ander toestel begonnen">
      <ul className="space-y-2">
        {elsewhere.map((game) => (
          <li
            key={game.id}
            className="flex items-center gap-3 rounded-xl bg-base-700/60 px-3 py-2.5"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate font-medium">
                {game.players.length > 0 ? game.players.join(', ') : 'Onbekende spelers'}
              </span>
              <span className="block text-sm text-slate-400">
                {GAME_LABEL[game.game] ?? game.game} · {formatDateTime(game.updatedAt)}
              </span>
            </span>
            <Button onClick={() => void take(game)} disabled={busy !== null}>
              {busy === game.id ? 'Bezig…' : 'Overnemen'}
            </Button>

            {/*
              And the other thing you might want with an old evening on the server:
              be rid of it. Without this the only way to delete one was to take it
              over first, which is a strange errand to run for something you do not
              want.
            */}
            <DeleteButton
              label="weg"
              onDelete={async () => {
                setError(null);
                try {
                  await removeGame(game.id, (game.game as ScoreKind) ?? 'boerenbridge');
                  setGames((all) => all.filter((one) => one.id !== game.id));
                } catch (err) {
                  setError(err instanceof Error ? err.message : 'Weggooien mislukt.');
                }
              }}
            />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-slate-500">
        Overnemen zet het potje op dit toestel en houdt de score hier bij. Het toestel waar het
        vandaan komt stopt met bijhouden.
      </p>
      {error && (
        <p role="alert" className="mt-2 text-sm text-bad">
          {error}
        </p>
      )}
    </Panel>
  );
}
